/* Klaviyo client API: only the public site ID belongs in this asset's configuration. */
(() => {
  if (customElements.get('bauer-back-in-stock')) return;

  class BauerBackInStock extends HTMLElement {
    connectedCallback() {
      // Section swaps connect custom elements before all their children are parsed.
      queueMicrotask(() => {
        if (!this.isConnected || this.controller) return;
        this.controller = new AbortController();
        this.product = this.closest('.product');
        this.dialog = this.querySelector('dialog');
        this.form = this.querySelector('form');
        this.trigger = this.querySelector('[data-bis-trigger]');
        this.status = this.querySelector('[role="status"]');
        this.submitButton = this.form.querySelector('[type="submit"]');
        this.variants = JSON.parse(this.querySelector('[data-bis-variants]').textContent);
        this.addButton = this.product?.querySelector('form[id="' + this.dataset.formId + '"] [name="add"]');
        const options = { signal: this.controller.signal };
        this.product?.addEventListener('change', () => this.sync(), options);
        this.trigger.addEventListener('click', () => this.open(), options);
        this.querySelector('[data-bis-close]').addEventListener('click', () => this.dialog.close(), options);
        this.form.addEventListener('submit', (event) => this.subscribe(event), options);
        this.sync();
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.request?.abort();
      if (this.dialog?.open) this.dialog.close();
      this.addButton?.removeAttribute('data-bis-hidden');
      this.controller = null;
    }

    selectedVariant() {
      const picker = this.product?.querySelector('variant-radios, variant-selects');
      if (picker) {
        // Read this product's controls directly; never trust a previous product's
        // global variant event or the hidden input left behind by an invalid choice.
        const options = picker.matches('variant-radios')
          ? Array.from(picker.querySelectorAll('fieldset'), (field) => field.querySelector('input:checked')?.value)
          : Array.from(picker.querySelectorAll('select'), (select) => select.value);
        return this.variants.find((variant) =>
          variant.options.length === options.length &&
          variant.options.every((value, index) => value === options[index]));
      }
      const id = this.product?.querySelector('form[id="' + this.dataset.formId + '"] [name="id"]')?.value;
      return this.variants.find((variant) => String(variant.id) === String(id));
    }

    sync() {
      const variant = this.selectedVariant();
      if (this.variant?.id !== variant?.id) {
        this.request?.abort();
        this.request = null;
        if (this.dialog.open) this.dialog.close();
        this.status.textContent = '';
        this.submitButton.disabled = false;
        this.form.removeAttribute('aria-busy');
      }
      this.variant = variant;
      const eligible = Boolean(variant && !variant.available && this.addButton);
      this.hidden = !eligible;
      this.addButton?.toggleAttribute('data-bis-hidden', eligible);
      this.querySelector('[data-bis-variant-title]').textContent =
        variant && variant.title !== 'Default Title' ? variant.title : '';
      return eligible;
    }

    open() {
      if (!this.sync()) return;
      this.status.textContent = '';
      this.submitButton.disabled = false;
      this.dialog.showModal();
      this.form.elements.email.focus();
    }

    async subscribe(event) {
      event.preventDefault();
      if (this.request || !this.sync() || !this.dialog.open || !this.form.reportValidity()) return;
      const variantId = this.variant.id;
      const request = new AbortController();
      this.request = request;
      this.submitButton.disabled = true;
      this.form.setAttribute('aria-busy', 'true');
      this.status.textContent = '';
      const timeout = setTimeout(() => request.abort(), 20000);
      try {
        const response = await fetch(
          'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(this.dataset.account),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', revision: '2026-07-15' },
            signal: request.signal,
            body: JSON.stringify({
              data: {
                type: 'back-in-stock-subscription',
                attributes: {
                  profile: { data: { type: 'profile', attributes: { email: this.form.elements.email.value.trim() } } },
                  channels: ['EMAIL']
                },
                relationships: {
                  variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } }
                }
              }
            })
          }
        );
        if (response.status !== 202) throw new Error('Subscription not accepted');
        if (this.isConnected && this.request === request) {
          this.status.textContent = this.dataset.success;
          this.form.elements.email.value = '';
        }
      } catch (_) {
        if (this.isConnected && this.request === request) {
          this.status.textContent = this.dataset.error;
          this.submitButton.disabled = false;
        }
      } finally {
        clearTimeout(timeout);
        if (this.request === request) {
          this.request = null;
          this.form.removeAttribute('aria-busy');
        }
      }
    }
  }

  customElements.define('bauer-back-in-stock', BauerBackInStock);
})();
